import { getGameRuntimeConfig } from "./config";
import type { ClientProfilePayload } from "./client-profile";
import { apiGet, apiPost } from "./http";
import { readMembership, type MembershipState } from "../membership";

const MODEL_PREFERENCE_KEY_PREFIX = "apk-games:model-preference";

export type ModelAccessStatus = {
  billing_status: string;
  label?: string;
  clickable: boolean;
  reason?: string;
  unavailable_reason?: string;
  available_pay_methods?: string[];
  moon_cost?: number;
  remaining_times?: number;
  used_times?: number;
  membership_id?: number;
  membership_tag_id?: number;
};

export type ModelTag = {
  id: number;
  name: string;
  slug: string;
};

export type ConversationMemoryConfig = {
  defaultRounds: number;
  maxRounds: number;
};

type ConversationMemoryConfigPayload = {
  default_rounds?: number;
  max_rounds?: number;
  default_history_rounds?: number;
  max_history_rounds?: number;
  defaultRounds?: number;
  maxRounds?: number;
};

export type ModelItem = {
  id: number;
  model_name: string;
  display_name: string;
  description: string;
  primary_category_id: number;
  primary_category_name: string;
  secondary_category_id: number;
  secondary_category_name: string;
  status: string;
  model_state: string;
  billing_mode: string;
  moon_cost: number;
  benefit_tags: ModelTag[];
  advanced_card_supported: boolean;
  access: ModelAccessStatus;
  dedicated: boolean;
  dedicated_label: string;
  sort: number;
  conversation_memory?: ConversationMemoryConfigPayload;
  memory_config?: ConversationMemoryConfigPayload;
};

export type ModelRuntimeState = "normal" | "busy" | "crowded";

export type ModelGroup = {
  id: number;
  name: string;
  models: ModelItem[];
};

export type ModelCategoryGroup = {
  id: number;
  name: string;
  children: ModelGroup[];
};

// 模型预校验携带当前身份的运营弹窗，避免调用前阻断时丢失后台文案。
export type QuotaExhaustedPopupPayload = {
  reason: string;
  title: string;
  content: string;
};

type ClientModelCenterPayload = {
  user?: ClientProfilePayload;
  models?: ModelCategoryGroup[];
  selected_model_id?: number;
  quota_exhausted_popup?: QuotaExhaustedPopupPayload;
  conversation_memory?: ConversationMemoryConfigPayload;
  memory_config?: ConversationMemoryConfigPayload;
  default_history_rounds?: number;
  max_history_rounds?: number;
};

type ClientModelCenterResponse = {
  data?: ClientModelCenterPayload;
};

export type ClientModelCenterResult = {
  user?: ClientProfilePayload;
  membership?: MembershipState;
  models: ModelCategoryGroup[];
  selectedModelId?: number;
  quotaExhaustedPopup?: QuotaExhaustedPopupPayload;
  conversationMemory?: ConversationMemoryConfig;
};

type ClientModelSelectionPayload = {
  package_id: number;
  model_id: number;
  updated_at: string;
};

type ClientModelSelectionResponse = {
  data?: ClientModelSelectionPayload;
};

export type ClientModelSelectionResult = {
  packageId: number;
  modelId: number;
  updatedAt: string;
};

function readPositiveInteger(value: unknown) {
  const numeric = Number(value);
  return Number.isInteger(numeric) && numeric > 0 ? numeric : undefined;
}

function normalizeConversationMemoryConfig(
  payload?: ConversationMemoryConfigPayload,
  fallbackPayload?: Pick<ClientModelCenterPayload, "default_history_rounds" | "max_history_rounds">
) {
  const maxRounds = readPositiveInteger(payload?.max_rounds ?? payload?.max_history_rounds ?? payload?.maxRounds ?? fallbackPayload?.max_history_rounds);
  const defaultRounds = readPositiveInteger(payload?.default_rounds ?? payload?.default_history_rounds ?? payload?.defaultRounds ?? fallbackPayload?.default_history_rounds);
  if (!maxRounds && !defaultRounds) return undefined;
  const resolvedMax = maxRounds ?? defaultRounds!;
  return {
    defaultRounds: Math.min(defaultRounds ?? resolvedMax, resolvedMax),
    maxRounds: resolvedMax
  } satisfies ConversationMemoryConfig;
}

export class NoAvailableModelError extends Error {
  modelCenter: ClientModelCenterResult;

  constructor(modelCenter: ClientModelCenterResult) {
    super("当前没有可用 AI 模型");
    this.name = "NoAvailableModelError";
    this.modelCenter = modelCenter;
  }
}

export class SelectedModelUnavailableError extends Error {
  modelCenter: ClientModelCenterResult;
  model: ModelItem;

  constructor(modelCenter: ClientModelCenterResult, model: ModelItem) {
    super(getModelUnavailableReason(model));
    this.name = "SelectedModelUnavailableError";
    this.modelCenter = modelCenter;
    this.model = model;
  }
}

export function flattenModelGroups(groups: ModelCategoryGroup[]) {
  return groups.flatMap((primary) => primary.children.flatMap((secondary) => secondary.models));
}

export function getConversationMemoryConfig(modelCenter: ClientModelCenterResult, modelId?: number) {
  const model = modelId ? flattenModelGroups(modelCenter.models).find((item) => item.id === modelId) : undefined;
  return normalizeConversationMemoryConfig(model?.conversation_memory ?? model?.memory_config)
    ?? modelCenter.conversationMemory;
}

export function getModelMoonCost(model: ModelItem) {
  return Math.max(0, model.access?.moon_cost ?? model.moon_cost ?? 0);
}

export function isPaidModel(model: ModelItem) {
  // 付费模型只由月亮消耗是否大于零决定，避免前端沿用已废弃的会员类型分支。
  return getModelMoonCost(model) > 0;
}

export function isModelQuotaExhausted(model: ModelItem) {
  const remaining = model.access?.remaining_times;
  const reason = model.access?.unavailable_reason || model.access?.reason || "";
  return remaining === 0 || reason.includes("次数已用完") || reason.includes("次数不足");
}

export function getModelRuntimeState(model: Pick<ModelItem, "model_state">): ModelRuntimeState {
  if (model.model_state === "busy" || model.model_state === "crowded") return model.model_state;
  return "normal";
}

export function getModelRuntimeStateLabel(model: Pick<ModelItem, "model_state">) {
  const state = getModelRuntimeState(model);
  if (state === "busy") return "繁忙";
  if (state === "crowded") return "拥挤";
  return "正常";
}

export function getModelUnavailableReason(model: ModelItem) {
  // 拥挤是后台明确配置的不可用状态，提示必须优先于通用权益或停用文案。
  if (getModelRuntimeState(model) === "crowded") return "模型当前拥挤，暂不可用，请切换其他模型";
  return model.access?.unavailable_reason || model.access?.reason || "当前模型不可用，请切换其他模型";
}

export function isModelCallable(model: ModelItem) {
  // 繁忙只用于向玩家提示等待风险；拥挤必须在共享运行时统一阻断，不能依赖各游戏自行判断。
  if (getModelRuntimeState(model) === "crowded") return false;
  // 免费模型次数耗尽后不可继续；付费模型即使次数为零，也要保留服务端扣月亮的调用机会。
  return model.access?.clickable !== false && (isPaidModel(model) || !isModelQuotaExhausted(model));
}

function normalizeModelState(model: ModelItem): ModelItem {
  const state = getModelRuntimeState(model);
  if (state !== "crowded") {
    return model.model_state === state ? model : { ...model, model_state: state };
  }
  // 统一收紧拥挤态的访问快照，使仍按 access.clickable 读取的旧游戏入口也不会选中该模型。
  return {
    ...model,
    model_state: state,
    access: {
      ...model.access,
      clickable: false,
      unavailable_reason: "模型当前拥挤，暂不可用，请切换其他模型",
    },
  };
}

function normalizeModelGroups(groups: ModelCategoryGroup[]) {
  return groups.map((primary) => ({
    ...primary,
    children: primary.children.map((secondary) => ({
      ...secondary,
      models: secondary.models.map(normalizeModelState),
    })),
  }));
}

function modelPreferenceKey(packageId: number) {
  return `${MODEL_PREFERENCE_KEY_PREFIX}:${packageId}`;
}

export function readPreferredModelId(packageId: number) {
  if (typeof localStorage === "undefined" || packageId <= 0) return undefined;
  try {
    const value = Number(localStorage.getItem(modelPreferenceKey(packageId)));
    return Number.isInteger(value) && value > 0 ? value : undefined;
  } catch {
    // 旧 WebView 隐私模式可能禁用 localStorage；模型解析仍可退回接口首项。
    return undefined;
  }
}

export function persistPreferredModelId(packageId: number, modelId: number) {
  if (typeof localStorage === "undefined" || packageId <= 0 || modelId <= 0) return;
  try {
    localStorage.setItem(modelPreferenceKey(packageId), String(modelId));
  } catch {
    // 本地偏好写入失败不能阻止本次已经解析成功的 LLM 调用。
  }
}

export function persistCurrentGameModelId(modelId: number) {
  persistPreferredModelId(getGameRuntimeConfig().packageId, modelId);
}

export class ModelClient {
  static async list(options: { token: string; packageId?: number; signal?: AbortSignal }): Promise<ClientModelCenterResult> {
    if (!options.token) throw new Error("missing client auth token");
    const config = getGameRuntimeConfig();
    const packageId = options.packageId ?? config.packageId;
    const query = packageId ? `?package_id=${encodeURIComponent(String(packageId))}` : "";
    const payload = await apiGet<ClientModelCenterResponse | ClientModelCenterPayload>(`/v1/client/models${query}`, {
      token: options.token,
      signal: options.signal
    });
    const data = "data" in payload && payload.data ? payload.data : payload as ClientModelCenterPayload;
    const membership = data.user ? readMembership(data.user) : undefined;
    const selectedModelId = readPositiveInteger(data.selected_model_id);
    if (selectedModelId) {
      // 服务端选择是跨设备真相源，本地只镜像它以兼容离线展示和门户入口的即时参数。
      persistPreferredModelId(packageId, selectedModelId);
    }

    return {
      user: data.user,
      membership,
      models: normalizeModelGroups(data.models ?? []),
      selectedModelId,
      quotaExhaustedPopup: data.quota_exhausted_popup,
      conversationMemory: normalizeConversationMemoryConfig(data.conversation_memory ?? data.memory_config, data)
    };
  }

  static async select(options: { token: string; modelId: number; packageId?: number; signal?: AbortSignal }): Promise<ClientModelSelectionResult> {
    if (!options.token) throw new Error("missing client auth token");
    const config = getGameRuntimeConfig();
    const packageId = options.packageId ?? config.packageId;
    if (packageId <= 0 || options.modelId <= 0) throw new Error("invalid model selection");
    const payload = await apiPost<ClientModelSelectionResponse | ClientModelSelectionPayload>("/v1/client/models/selection", {
      token: options.token,
      signal: options.signal,
      body: { package_id: packageId, model_id: options.modelId }
    });
    const data = "data" in payload && payload.data ? payload.data : payload as ClientModelSelectionPayload;
    const modelId = readPositiveInteger(data.model_id);
    if (!modelId) throw new Error("invalid model selection response");
    persistPreferredModelId(packageId, modelId);
    return { packageId, modelId, updatedAt: data.updated_at };
  }

  static async resolveAvailable(options: { token: string; packageId?: number; preferredModelId?: number; fallbackModelId?: number; signal?: AbortSignal }) {
    const config = getGameRuntimeConfig();
    const packageId = options.packageId ?? config.packageId;
    const modelCenter = await ModelClient.list({ token: options.token, packageId, signal: options.signal });
    const models = flattenModelGroups(modelCenter.models);
    const savedModelId = readPreferredModelId(packageId);
    // 服务端 package 级偏好优先于本地镜像和游戏存档，跨设备切换后不能被旧状态反向覆盖。
    const explicitModel = models.find((item) => item.id === modelCenter.selectedModelId)
      ?? models.find((item) => item.id === savedModelId)
      ?? models.find((item) => item.id === options.preferredModelId);
    if (explicitModel && !isModelCallable(explicitModel)) {
      throw new SelectedModelUnavailableError(modelCenter, explicitModel);
    }
    const model = explicitModel
      ?? models.find((item) => item.id === options.fallbackModelId && isModelCallable(item))
      ?? models.find(isModelCallable);
    if (!model) throw new NoAvailableModelError(modelCenter);
    persistPreferredModelId(packageId, model.id);
    return { modelCenter, model };
  }
}
